/**
 * The login/register form behind both AuthGate and UserMenu.
 *
 * The two differ only in what they do around a successful sign-in and around
 * an OAuth error coming back in the URL, so those are the two callbacks; the
 * fields, the validation and the requests are shared.
 */

import { useEffect, useState } from "react";
import { useViewerStore } from "../store";
import { apiClient, ApiClientError } from "../api";
import type { AuthConfig } from "../api";

export type AuthView = "login" | "register";

export interface UseAuthFormOptions {
  /** Run after the user is signed in or registered. */
  onAuthenticated?: () => void;
  /** Run when the page loads carrying an ?auth_error= from an OAuth redirect. */
  onAuthError?: () => void;
}

export function useAuthForm({ onAuthenticated, onAuthError }: UseAuthFormOptions = {}) {
  const setUser = useViewerStore((state) => state.setUser);
  const setUserLoading = useViewerStore((state) => state.setUserLoading);

  const [authView, setAuthView] = useState<AuthView>("login");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);

  // Login form
  const [loginId, setLoginId] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register form
  const [regUsername, setRegUsername] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirmPassword, setRegConfirmPassword] = useState("");
  const [regDisplayName, setRegDisplayName] = useState("");

  // Fetch current user and auth config on mount
  useEffect(() => {
    const init = async () => {
      setUserLoading(true);
      try {
        const [userInfo, config] = await Promise.all([
          apiClient.getCurrentUser(),
          apiClient.getAuthConfig().catch(() => null),
        ]);
        setUser(userInfo.authenticated ? userInfo : null);
        if (config) {
          setAuthConfig(config);
        }
      } catch {
        setUser(null);
      } finally {
        setUserLoading(false);
      }
    };
    init();
  }, [setUser, setUserLoading]);

  // Check for OAuth error in URL (from redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get("auth_error");
    if (authError) {
      setError(`Login failed: ${authError.replace(/_/g, " ")}`);
      onAuthError?.();
      const url = new URL(window.location.href);
      url.searchParams.delete("auth_error");
      window.history.replaceState({}, "", url.toString());
    }
    // Runs once on mount, as it did in both components before.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForms = () => {
    setLoginId("");
    setLoginPassword("");
    setRegUsername("");
    setRegEmail("");
    setRegPassword("");
    setRegConfirmPassword("");
    setRegDisplayName("");
    setError(null);
  };

  const switchView = (view: AuthView) => {
    resetForms();
    setAuthView(view);
  };

  const handleLogin = async () => {
    setError(null);
    if (!loginId || !loginPassword) {
      setError("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const userInfo = await apiClient.login({
        username_or_email: loginId,
        password: loginPassword,
      });
      setUser(userInfo);
      onAuthenticated?.();
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.detail || "Login failed" : "Login failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    if (!regUsername || !regEmail || !regPassword) {
      setError("Please fill in all required fields");
      return;
    }
    if (regPassword !== regConfirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (regPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const userInfo = await apiClient.register({
        username: regUsername,
        email: regEmail,
        password: regPassword,
        display_name: regDisplayName || undefined,
      });
      setUser(userInfo);
      onAuthenticated?.();
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.detail || "Registration failed"
          : "Registration failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return {
    authView,
    setAuthView,
    submitting,
    error,
    setError,
    authConfig,
    loginId,
    setLoginId,
    loginPassword,
    setLoginPassword,
    regUsername,
    setRegUsername,
    regEmail,
    setRegEmail,
    regPassword,
    setRegPassword,
    regConfirmPassword,
    setRegConfirmPassword,
    regDisplayName,
    setRegDisplayName,
    resetForms,
    switchView,
    handleLogin,
    handleRegister,
  };
}
